import { useState } from "react";
import type { PhishingLoginConfig } from "../scenario/types";

/**
 * The spoofed / hijacked login page. Looks legitimate; the teach moment dissects
 * the URL after submission.
 *
 * Usability: a careful learner often freezes here — "do I invent credentials, or
 * leave them blank?". So we simulate a browser password-manager:
 *   - normal (real-looking) domain: clicking a field offers a SAVED login that
 *     autofills both fields in one click -> Sign In is the obvious next step.
 *   - spoofed / typo domain (config.autofill.noMatch): the manager pops up but has
 *     NO entry for this site (the real-world red flag) and tells the learner to
 *     type any credentials to proceed.
 * Sign In always works (empty fields fall back to a demo identity) so the learner
 * can never get truly stuck.
 */
export function PhishingLogin({
  config,
  onSubmit,
}: {
  config: PhishingLoginConfig;
  onSubmit: (fields: { email: string; password: string }) => void;
}) {
  const af = config.autofill;
  // Derive a plausible saved identity from the imitated domain when not supplied.
  const domain = (config.legitDomain || config.brand || "company")
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "")
    .replace(/[^a-z0-9.]/g, "");
  // The host actually shown in the address bar (used for the "no saved login" red
  // flag) — this is the SPOOF host, not the imitated legit domain.
  const spoofHost = (config.spoofUrl || domain)
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "");
  const savedEmail = af?.email || `alice@${domain.includes(".") ? domain : `${domain}.com`}`;
  const manager = af?.manager || "Saved logins";
  const accountLabel = af?.accountLabel || config.brand || config.headline || "Your account";
  const noMatch = !!af?.noMatch;
  const hasHelper = !!af;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [suggest, setSuggest] = useState(false);
  const [filled, setFilled] = useState(false);

  const openSuggest = () => {
    if (hasHelper && !filled) setSuggest(true);
  };
  const applyAutofill = () => {
    setEmail(savedEmail);
    setPassword("\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022");
    setFilled(true);
    setSuggest(false);
  };
  const doSubmit = (e?: { preventDefault?: () => void }) => {
    e?.preventDefault?.();
    onSubmit({ email: email || savedEmail, password: password || "\u2022\u2022\u2022\u2022\u2022" });
  };

  // The instructional hint under the title.
  const hint = !hasHelper
    ? "Enter any email and password, then Sign In."
    : noMatch
      ? "Your password manager has no saved login for this site \u2014 type any email and password, then Sign In."
      : filled
        ? "Saved login filled \u2014 click Sign In Securely."
        : "Click the email field \u2014 your saved login will appear. Pick it to fill the form.";

  // Highlight the field the learner should click first (until they engage with it).
  const cue = hasHelper && !noMatch && !filled;

  return (
    <div className="phish">
      {/*
        Chrome/Edge "Save password?" is suppressed with non-login autocomplete
        tokens + decoy names. The in-sim password manager UI is separate.
      */}
      <form
        className="card"
        onSubmit={doSubmit}
        autoComplete="off"
        data-lpignore="true"
        data-1p-ignore="true"
        data-bwignore="true"
        data-form-type="other"
      >
        <h2>{config.headline ?? "Welcome Back"}</h2>
        <div className="sub">Sign in to access your account securely</div>

        <div className={`phish-hint ${cue ? "go" : ""}`} role="status">
          <span className="phish-hint-ico" aria-hidden>
            {noMatch ? "\u26A0" : "\u27A4"}
          </span>
          <span>{hint}</span>
        </div>

        <div className="phish-fields">
          <label htmlFor="phish-email">Email address</label>
          <input
            id="phish-email"
            className={`phish-input ${cue ? "cue" : ""}`}
            type="text"
            name="cf_sim_email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onFocus={openSuggest}
            onClick={openSuggest}
            autoComplete="off"
            data-lpignore="true"
            data-1p-ignore="true"
            data-form-type="other"
          />

          <label htmlFor="phish-pw">Password</label>
          <input
            id="phish-pw"
            className={`phish-input cf-mask ${cue ? "cue" : ""}`}
            type="text"
            name="cf_sim_secret"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onFocus={openSuggest}
            onClick={openSuggest}
            autoComplete="new-password"
            data-lpignore="true"
            data-1p-ignore="true"
            data-form-type="other"
          />

          {suggest && (
            <div
              className={`pwmgr ${noMatch ? "pwmgr-static" : ""}`}
              role="dialog"
              aria-label="Saved logins"
            >
              <div className="pwmgr-head">
                <span className="pwmgr-key" aria-hidden>
                  {"\u{1F511}"}
                </span>
                {manager}
              </div>
              {noMatch ? (
                <div className="pwmgr-nomatch">
                  <span className="pwmgr-warn" aria-hidden>
                    {"\u26A0"}
                  </span>
                  <div>
                    <div className="pwmgr-nomatch-title">No saved login for this site</div>
                    <div className="pwmgr-nomatch-sub">
                      Your password manager doesn&rsquo;t recognise <strong>{spoofHost}</strong>.
                    </div>
                  </div>
                </div>
              ) : (
                <button type="button" className="pwmgr-item" onClick={applyAutofill}>
                  <span className="pwmgr-ava" aria-hidden>
                    {"\u{1F464}"}
                  </span>
                  <span className="pwmgr-id">
                    <span className="pwmgr-id-name">{accountLabel}</span>
                    <span className="pwmgr-id-mail">{savedEmail}</span>
                  </span>
                  <span className="pwmgr-fill">Fill</span>
                </button>
              )}
            </div>
          )}
        </div>

        <button className={`btn submit ${filled ? "blink" : ""}`} type="submit">
          Sign In Securely
        </button>
        <div
          style={{
            display: "flex",
            justifyContent: "space-around",
            marginTop: 18,
            color: "#6b7682",
            fontSize: 11,
          }}
        >
          <span>{"\u{1F512}"} 256-bit SSL</span>
          <span>{"\u{1F6E1}\uFE0F"} FDIC Insured</span>
          <span>{"\u{1F511}"} 2FA Enabled</span>
        </div>
      </form>
    </div>
  );
}
